import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { fanHomeApi, apiMessage } from "./fanHomeApi";
import { discoveryApi } from "./discoveryApi";
import { useErrorToast } from "../common/errorToast";
import MusicShell from "./MusicShell";
import TrackCard from "./TrackCard";
import { CARD_GRID } from "./Section";
import { LoadMore, GridSkeleton, Notice } from "./discoveryParts";

// Charts (web 2.4.5): Top tracks, Trending (rising) and New releases, for a day, week or month, optionally in one genre.
// The choices live in the address (?tab=&period=&genre=<genre id>) so a chart can be shared and the back button works.
const TABS = [["top", "Top tracks"], ["trending", "Trending"], ["new", "New"]];
const PERIODS = [["day", "Today"], ["week", "This week"], ["month", "This month"]];
const PAGE_SIZE = 20;
const SUBTITLES = {
  top: "The most played sounds",
  trending: "Sounds that are climbing the fastest",
  new: "The newest releases on Nyawoo",
};

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button" onClick={onClick} aria-pressed={active}
      className={`shrink-0 rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition ${active ? "text-white" : "bg-white text-gray-800 hover:bg-gray-100 border-gray-300"}`}
      style={active ? { backgroundColor: ACCENT, borderColor: ACCENT } : undefined}
    >{children}</button>
  );
}

export default function Charts() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "top";
  const period = PERIODS.some(([k]) => k === params.get("period")) ? params.get("period") : "week";
  const genre = Number(params.get("genre")) > 0 ? Number(params.get("genre")) : 0;

  const [genres, setGenres] = useState([]);
  const [state, setState] = useState({ status: "loading", error: "", items: [], page: 1, hasMore: false, busy: false });
  useErrorToast(state.status === "ready" ? state.error : ""); // a failed "Load more" is a popup; a failed first load keeps the Try again panel
  const ticket = useRef(0);

  useEffect(() => {
    let live = true;
    discoveryApi.filters().then((d) => live && setGenres(d?.genres || [])).catch(() => {});
    return () => { live = false; };
  }, []);

  const query = useCallback((page) => ({ tab, period, genre_id: genre || undefined, page, limit: PAGE_SIZE }), [tab, period, genre]);

  const load = useCallback(async () => {
    const mine = ++ticket.current;
    setState({ status: "loading", error: "", items: [], page: 1, hasMore: false, busy: false });
    try {
      const data = await fanHomeApi.charts(query(1));
      if (mine === ticket.current) setState({ status: "ready", error: "", items: data?.items || [], page: 1, hasMore: !!data?.has_more, busy: false });
    } catch (e) {
      if (mine === ticket.current) setState({ status: "error", error: apiMessage(e, "We couldn't load the charts."), items: [], page: 1, hasMore: false, busy: false });
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    const mine = ticket.current;
    const next = state.page + 1;
    setState((s) => ({ ...s, busy: true, error: "" }));
    try {
      const data = await fanHomeApi.charts(query(next));
      if (mine !== ticket.current) return;
      setState((s) => ({ ...s, busy: false, page: next, hasMore: !!data?.has_more, items: [...s.items, ...(data?.items || []).filter((t) => !s.items.some((x) => x.id === t.id))] }));
    } catch (e) {
      if (mine === ticket.current) setState((s) => ({ ...s, busy: false, error: apiMessage(e, "We couldn't load more tracks.") }));
    }
  };

  const update = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  const genreName = genres.find((g) => g.id === genre)?.name;
  const { status, items } = state;

  return (
    <MusicShell>
      <h1 className="text-2xl font-bold text-gray-900">Charts</h1>
      <p className="mt-1 text-sm text-gray-500">{SUBTITLES[tab]}{genreName ? ` in ${genreName}` : ""}</p>

      <div className="mt-5 flex gap-2 border-b border-gray-200" role="tablist" aria-label="Chart">
        {TABS.map(([key, label]) => (
          <button
            key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => update({ tab: key === "top" ? "" : key })}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === key ? "text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            style={tab === key ? { borderColor: ACCENT } : undefined}
          >{label}</button>
        ))}
      </div>

      <div className="mt-4 space-y-4">
        {tab !== "new" && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Period</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Period">
              {PERIODS.map(([key, label]) => <Chip key={key} active={period === key} onClick={() => update({ period: key === "week" ? "" : key })}>{label}</Chip>)}
            </div>
          </div>
        )}
        {genres.length > 0 && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Genre</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Genre">
              <Chip active={!genre} onClick={() => genre && update({ genre: "" })}>All</Chip>
              {genres.map((g) => <Chip key={g.id} active={genre === g.id} onClick={() => update({ genre: genre === g.id ? "" : String(g.id) })}>{g.name}</Chip>)}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6" aria-live="polite">
        {status === "loading" && <GridSkeleton count={8} />}
        {status === "error" && (
          <Notice action={<button type="button" onClick={load} className="mt-3 rounded-full font-semibold text-white px-5 py-2 text-sm" style={{ backgroundColor: ACCENT }}>Try again</button>}>
            {state.error}
          </Notice>
        )}
        {status === "ready" && !items.length && (
          <Notice>{tab === "new" ? "No new releases here yet." : "No plays have been counted for this chart yet. Check back soon."}</Notice>
        )}
        {status === "ready" && items.length > 0 && (
          <>
            <div className={CARD_GRID} data-testid="chart-list">
              {items.map((t, _i, all) => (
                <TrackCard key={t.id} track={t} badge={`#${t.rank}`} showPlays={tab !== "new"} showReleased={tab === "new"} queue={all} />
              ))}
            </div>
            {state.hasMore && <LoadMore onClick={loadMore} busy={state.busy} />}
          </>
        )}
      </div>
    </MusicShell>
  );
}
